import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { STAGES, STAGE_LABELS, TARGET_LABELS, ACTION_LABELS, SOURCE_LABELS } from "../lib/constants";
import { LevelBadge } from "../components/LevelBadge";
import NewLeadDialog from "../components/NewLeadDialog";
import { Loader2, ChevronRight, Send, Eye, PenLine, CheckCircle2, Skull, Search, Clock, PhoneCall, MapPin, MessageSquare, FileText, Handshake, FileSignature, StickyNote, UserCheck, CalendarClock, Cog, FolderPlus, Zap, Plus, Star, Trophy, Archive, Undo2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import QualityWarningDialog from "../components/QualityWarningDialog";

const ACTION_OPTIONS = ["chiamata", "sopralluogo", "consulenza", "preventivo", "trattativa", "contratto", "nota"];
const FUNNEL_LABELS = { pre_outlook: "Pre-Outlook", lead: "Parcheggio Lead", progetto: "Preventivo", parcheggio: "Parcheggio" };

const TIMELINE_META = {
  chiamata: { icon: PhoneCall, color: "#5B8DEF" },
  sopralluogo: { icon: MapPin, color: "#0D9488" },
  consulenza: { icon: MessageSquare, color: "#0EA5A5" },
  preventivo: { icon: FileText, color: "#3B82F6" },
  trattativa: { icon: Handshake, color: "#E08A3C" },
  contratto: { icon: FileSignature, color: "#16A34A" },
  nota: { icon: StickyNote, color: "#6B7280" },
  profilazione: { icon: UserCheck, color: "#8B5CF6" },
  appuntamento: { icon: CalendarClock, color: "#F59E0B" },
  sistema: { icon: Cog, color: "#64748B" },
  apertura: { icon: FolderPlus, color: "#FDD003" },
};

function LeadActionsDialog({ open, onOpenChange, lead, entries, loading, newAction, setNewAction, onSubmit, adding, highlightedId, onHighlight }) {
  const [filter, setFilter] = useState("all");
  useEffect(() => setFilter("all"), [lead?.id]);
  const types = Array.from(new Set(entries.map((e) => e.type)));
  const shown = filter === "all" ? entries : entries.filter((e) => e.type === filter);
  const typeLabel = (t) => t === "all" ? "Tutte" : t === "apertura" ? "Apertura" : t === "automazione" ? "Automazioni" : (ACTION_LABELS[t] || t);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg" data-testid="lead-actions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Azioni · {lead?.name}</DialogTitle>
          <DialogDescription className="sr-only">Elenco delle azioni registrate sul lead</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-2.5" data-testid="quick-action-form">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={newAction.type} onValueChange={(v) => setNewAction((a) => ({ ...a, type: v }))}>
              <SelectTrigger className="w-[160px] h-9" data-testid="quick-action-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION_OPTIONS.map((t) => <SelectItem key={t} value={t}>{ACTION_LABELS[t]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea value={newAction.note} onChange={(e) => setNewAction((a) => ({ ...a, note: e.target.value }))} placeholder="Descrizione dell'azione, esito, prossimi passi..." rows={2} data-testid="quick-action-note" />
          <div className="flex justify-end">
            <Button size="sm" onClick={() => onSubmit(false, null)} disabled={adding} className="font-semibold" data-testid="quick-action-submit">
              {adding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Registra azione
            </Button>
          </div>
        </div>
        {entries.length > 0 && types.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="actions-filter">
            {["all", ...types].map((t) => (
              <button key={t} onClick={() => setFilter(t)} data-testid={`actions-filter-${t}`}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${filter === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {typeLabel(t)}
              </button>
            ))}
          </div>
        )}
        <div className="max-h-[45vh] overflow-y-auto pr-1 mt-1">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : shown.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">{entries.length === 0 ? "Nessuna azione registrata." : "Nessuna azione di questo tipo."}</p>
          ) : (
            <div className="space-y-4 py-1">
              {shown.map((a) => {
                const meta = TIMELINE_META[a.type] || { icon: Zap, color: "#64748B" };
                const Icon = meta.icon;
                const label = a.type === "apertura" ? "Apertura lead" : (ACTION_LABELS[a.type] || a.type);
                return (
                  <div key={a.id} className="flex gap-3" data-testid={`lead-action-${a.id}`}>
                    <div className="mt-0.5 h-8 w-8 shrink-0 rounded flex items-center justify-center" style={{ backgroundColor: `${meta.color}1A` }}>
                      <Icon className="h-4 w-4" strokeWidth={1.5} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 border-b border-border pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{label}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><Clock className="h-3 w-3" /> {new Date(a.created_at).toLocaleString("it-IT")}</span>
                      </div>
                      {a.note && <p className="text-sm text-muted-foreground mt-1">{a.note}</p>}
                      {a.created_by_name && <span className="text-xs text-muted-foreground">— {a.created_by_name}</span>}
                      {a.id !== "__apertura__" && (
                        <div className="mt-1.5">
                          <button onClick={() => onHighlight(a.id)} data-testid={`highlight-action-${a.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${highlightedId === a.id ? "bg-amber-500/15 text-amber-600 border-amber-500/40" : "border-border text-muted-foreground hover:text-amber-600 hover:border-amber-500/40"}`}>
                            <Star className={`h-3 w-3 ${highlightedId === a.id ? "fill-amber-500 text-amber-500" : ""}`} /> {highlightedId === a.id ? "In evidenza" : "Metti in evidenza"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FUNNEL_STEPS = [
  { key: "inviate", label: "Inviate", icon: Send, color: "text-muted-foreground", pctKey: null },
  { key: "aperte", label: "Aperte", icon: Eye, color: "text-blue-500", pctKey: "pct_aperte" },
  { key: "firmate", label: "Firmate", icon: PenLine, color: "text-emerald-500", pctKey: "pct_firmate" },
  { key: "pagate", label: "Pagate", icon: CheckCircle2, color: "text-green-600", pctKey: "pct_pagate" },
];

function ProposteFunnel({ data }) {
  if (!data) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6" data-testid="proposte-funnel">
      <div className="flex items-center justify-between mb-3 gap-2">
        <span className="text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">Funnel proposte</span>
        <span className="text-[10px] text-muted-foreground shrink-0">% sul totale inviate</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {FUNNEL_STEPS.map((step) => (
          <div key={step.key} className="rounded-lg border border-border bg-secondary/20 px-3 py-2.5" data-testid={`funnel-${step.key}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <step.icon className={`h-3.5 w-3.5 ${step.color}`} strokeWidth={2} />
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium truncate">{step.label}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold" data-testid={`funnel-${step.key}-count`}>{data[step.key] ?? 0}</span>
              {step.pctKey && <span className={`text-xs font-semibold ${step.color}`}>{data[step.pctKey] ?? 0}%</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Pipeline({ embedded = false, focusStage = null }) {
  const [leads, setLeads] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [actionsLead, setActionsLead] = useState(null);
  const [actionsEntries, setActionsEntries] = useState([]);
  const [actionsHighlightedId, setActionsHighlightedId] = useState(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [newAction, setNewAction] = useState({ type: "chiamata", note: "" });
  const [addingAction, setAddingAction] = useState(false);
  const [qualityWarn, setQualityWarn] = useState(null);
  const [enrollMap, setEnrollMap] = useState({});
  const [enabledWfs, setEnabledWfs] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [parkFilter, setParkFilter] = useState("active");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "team_leader"].includes(user?.role);

  const load = (pf = parkFilter) => {
    const p = pf === "parked" ? "?parking=true" : pf === "all" ? "?include_parked=true" : "";
    return api.get(`/leads${p}`).then((r) => setLeads(r.data));
  };
  const loadFunnel = () => api.get("/stats/proposte-funnel").then((r) => setFunnel(r.data)).catch(() => {});
  const loadEnroll = () => {
    api.get("/automations/enrollments/active-summary").then((r) => setEnrollMap(r.data || {})).catch(() => {});
    api.get("/automations/workflows/enabled").then((r) => setEnabledWfs(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load(); loadFunnel(); loadEnroll();
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "partner_commerciale"))).catch(() => {});
  }, []);

  const enrollLead = async (leadId, workflowId) => {
    try {
      await api.post(`/automations/leads/${leadId}/enroll`, { workflow_id: workflowId });
      toast.success("Lead iscritto al funnel");
      loadEnroll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore iscrizione funnel"); }
  };

  const openActions = async (lead) => {
    setActionsLead(lead);
    setActionsEntries([]);
    setNewAction({ type: "chiamata", note: "" });
    setActionsLoading(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}`);
      setActionsEntries(buildEntries(data));
      setActionsHighlightedId(data.highlighted_action_id || null);
    } catch (e) {
      toast.error("Errore nel caricamento delle azioni");
    } finally {
      setActionsLoading(false);
    }
  };

  const buildEntries = (leadData) => {
    const entries = [...(leadData.timeline || [])];
    if (leadData.created_at) {
      entries.push({
        id: "__apertura__",
        type: "apertura",
        note: `Lead aperto${leadData.source ? ` · fonte ${SOURCE_LABELS[leadData.source] || leadData.source}` : ""}.`,
        created_at: leadData.created_at,
        created_by_name: leadData.created_by_name,
      });
    }
    entries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return entries;
  };

  const submitAction = async (force, quality) => {
    if (!actionsLead) return;
    if (!newAction.note.trim()) { toast.error("Inserisci una descrizione"); return; }
    setAddingAction(true);
    try {
      const { data } = await api.post(`/leads/${actionsLead.id}/actions`, { type: newAction.type, note: newAction.note, force: !!force, quality: quality || null });
      if (data.needs_improvement) {
        setQualityWarn({ ...data });
        return;
      }
      setActionsEntries(buildEntries(data.lead));
      setQualityWarn(null);
      setNewAction({ type: "chiamata", note: "" });
      toast.success("Azione registrata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella registrazione");
    } finally {
      setAddingAction(false);
    }
  };

  const toggleHighlight = async (actionId) => {
    if (!actionsLead) return;
    try {
      const { data } = await api.post(`/leads/${actionsLead.id}/actions/${actionId}/highlight`);
      setActionsHighlightedId(data.highlighted_action_id || null);
      setLeads((ls) => ls.map((l) => (l.id === actionsLead.id ? data.lead : l)));
      toast.success(data.highlighted_action_id ? "Azione messa in evidenza" : "Evidenza rimossa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella messa in evidenza");
    }
  };

  const move = async (lead, stage) => {
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, stage } : l)));
    try {
      await api.post(`/leads/${lead.id}/stage`, { stage });
      toast.success(`${lead.name} → ${STAGE_LABELS[stage]}`);
    } catch (e) {
      toast.error("Errore nel cambio fase");
      load();
    }
  };

  const setParking = async (lead, parking) => {
    try {
      await api.post(`/leads/${lead.id}/park`, { parking });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success(parking ? `${lead.name} messo in parcheggio` : `${lead.name} riportato in pipeline`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore parcheggio"); }
  };

  const sendToCemetery = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/cemetery`, { reason: "" });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success(`${lead.name} mandato al cimitero`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio al cimitero");
    }
  };

  if (!leads) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const baseLeads = embedded
    ? leads.filter((l) => !(l.is_free || String(l.source || "").startsWith("free") || ((l.channel === "rete" || l.source === "rete") && !l.rete_unlocked)))
    : leads;
  const shownLeads = userFilter === "all"
    ? baseLeads
    : userFilter === "unassigned"
      ? baseLeads.filter((l) => !l.assigned_to)
      : baseLeads.filter((l) => l.assigned_to === userFilter);

  return (
    <div>
      <header className={embedded ? "flex items-center justify-between gap-2 pb-3 pr-8 border-b border-border" : "sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8"}>
        <div>
          <h1 className="text-xl font-heading font-bold">Pipeline Commerciale</h1>
          <p className="text-xs text-muted-foreground">{embedded ? "Esclusi Area Free e Area Rete · modifiche salvate subito" : "Dal lead al contratto"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={parkFilter} onValueChange={(v) => { setParkFilter(v); load(v); }}>
            <SelectTrigger className="w-[150px] h-9" data-testid="pipeline-park-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active" data-testid="park-filter-active">Attivi</SelectItem>
              <SelectItem value="parked" data-testid="park-filter-parked">Parcheggiati</SelectItem>
              <SelectItem value="all" data-testid="park-filter-all">Tutti</SelectItem>
            </SelectContent>
          </Select>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="pipeline-user-filter"><SelectValue placeholder="Filtra per utente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="pipeline-user-filter-all">Tutti gli utenti</SelectItem>
              <SelectItem value="unassigned" data-testid="pipeline-user-filter-unassigned">Non assegnati</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`pipeline-user-filter-${u.id}`}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          {!embedded && <NewLeadDialog onCreated={load} />}
        </div>
      </header>

      <div className={embedded ? "pt-4" : "p-4 lg:p-8"}>
        {!embedded && <ProposteFunnel data={funnel} />}
        <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {STAGES.map((stage) => {
            const items = shownLeads.filter((l) => l.stage === stage);
            return (
              <div key={stage} ref={stage === focusStage ? (el) => el && el.scrollIntoView({ inline: "center", block: "nearest" }) : undefined}
                className={`w-72 shrink-0 ${stage === focusStage ? "rounded-lg ring-2 ring-primary p-2 bg-primary/5" : ""}`} data-testid={`column-${stage}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground">{STAGE_LABELS[stage]}</span>
                  <span className="text-xs bg-secondary rounded px-1.5 py-0.5 text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2 min-h-[120px]">
                  {items.map((l) => (
                    <div key={l.id} data-testid={`card-${l.id}`} className="border border-border rounded bg-card p-3 hover:border-primary/40 transition-colors group">
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => navigate(`/leads/${l.id}`)} className="font-medium text-sm text-left hover:text-primary transition-colors">{l.name}</button>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {l.in_gara && (
                            <span title="Lead in gara installatori" data-testid={`gara-${l.id}`} className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/15 border border-amber-500/40">
                              <Trophy className="h-3 w-3 text-amber-500" strokeWidth={2} />
                            </span>
                          )}
                          <button onClick={() => openActions(l)} data-testid={`view-actions-${l.id}`} title="Vedi azioni del lead" className="text-muted-foreground hover:text-primary transition-colors"><Search className="h-3.5 w-3.5" /></button>
                          <LevelBadge level={l.level} />
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{TARGET_LABELS[l.target]}</div>
                      {l.contract?.commessa && (
                        <div className="mt-1.5" data-testid={`commessa-${l.id}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-600/10 text-green-700 border border-green-600/30">
                            <FileSignature className="h-3 w-3" /> Commessa {l.contract.commessa}
                          </span>
                        </div>
                      )}
                      {l.highlighted_action && (
                        <div className="mt-2 flex items-start gap-1.5 rounded bg-amber-500/10 border border-amber-500/30 px-2 py-1.5" data-testid={`highlight-${l.id}`}>
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">{ACTION_LABELS[l.highlighted_action.type] || l.highlighted_action.type}</span>
                              {l.highlighted_action.created_at && <span className="text-[10px] text-muted-foreground">· {new Date(l.highlighted_action.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>}
                            </div>
                            {l.highlighted_action.note && <p className="text-[11px] text-foreground/80 leading-snug line-clamp-2">{l.highlighted_action.note}</p>}
                          </div>
                        </div>
                      )}
                      {(() => {
                        const en = enrollMap[l.id];
                        if (en) return (
                          <div className="mt-2" data-testid={`funnel-badge-${l.id}`}>
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                              <Zap className="h-3 w-3" /> Funnel {FUNNEL_LABELS[en.funnel_type] || en.funnel_type}{en.status === "paused" ? " · in pausa" : ""}
                            </span>
                          </div>
                        );
                        if (enabledWfs.length > 0) return (
                          <div className="mt-2" data-testid={`funnel-enroll-${l.id}`}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary border border-dashed border-border rounded px-1.5 py-0.5" data-testid={`funnel-enroll-btn-${l.id}`}><Plus className="h-3 w-3" /> Attiva funnel</button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                {enabledWfs.map((w) => <DropdownMenuItem key={w.id} onClick={() => enrollLead(l.id, w.id)} data-testid={`enroll-${l.id}-${w.id}`}>{w.name}</DropdownMenuItem>)}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                        return null;
                      })()}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-muted-foreground">{l.assigned_to_name || "Non assegnato"}</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`move-${l.id}`} className="text-xs flex items-center gap-0.5 text-primary hover:underline">{isAdmin ? "Sposta" : "Azioni"} <ChevronRight className="h-3 w-3" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isAdmin && STAGES.filter((s) => s !== stage).map((s) => (
                              <DropdownMenuItem key={s} onClick={() => move(l, s)} data-testid={`move-${l.id}-${s}`}>{STAGE_LABELS[s]}</DropdownMenuItem>
                            ))}
                            {isAdmin && <DropdownMenuSeparator />}
                            {(l.parking ?? parkFilter === "parked")
                              ? <DropdownMenuItem onClick={() => setParking(l, false)} data-testid={`unpark-${l.id}`}><Undo2 className="h-3.5 w-3.5 mr-1.5" /> Riporta in pipeline</DropdownMenuItem>
                              : <DropdownMenuItem onClick={() => setParking(l, true)} data-testid={`park-${l.id}`}><Archive className="h-3.5 w-3.5 mr-1.5" /> Parcheggia</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => sendToCemetery(l)} data-testid={`cemetery-${l.id}`} className="text-destructive focus:text-destructive"><Skull className="h-3.5 w-3.5 mr-1.5" /> Manda al cimitero</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
      <LeadActionsDialog open={!!actionsLead} onOpenChange={(o) => { if (!o) { setActionsLead(null); setQualityWarn(null); } }} lead={actionsLead} entries={actionsEntries} loading={actionsLoading} newAction={newAction} setNewAction={setNewAction} onSubmit={submitAction} adding={addingAction} highlightedId={actionsHighlightedId} onHighlight={toggleHighlight} />
      <QualityWarningDialog data={qualityWarn} saving={addingAction} onModifica={() => setQualityWarn(null)} onSalvaComunque={() => submitAction(true, qualityWarn?.quality)} />
    </div>
  );
}
